import React from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, ListBullets, Scales, Stack, Warning } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import type { IContaCilLancamentoResumo } from '../../../../../../hooks/useContaCilLancamentos';
import { fmtMoeda, fonteValorKpi } from '../shared';

export type FiltroStatus = '' | 'conciliado' | 'pendente' | 'revisao';

interface KpiDef {
  value: FiltroStatus;
  label: string;
  countKey: 'total' | 'conciliados' | 'pendentes' | 'revisao';
  icon: React.ReactNode;
  accent: string;
  atencao?: boolean;
}

const KPIS: KpiDef[] = [
  {
    value: '',
    label: 'Total',
    countKey: 'total',
    icon: <ListBullets size={20} weight="duotone" />,
    accent: '#6366F1',
  },
  {
    value: 'conciliado',
    label: 'Conciliados',
    countKey: 'conciliados',
    icon: <CheckCircle size={20} weight="duotone" />,
    accent: '#10B981',
  },
  {
    value: 'pendente',
    label: 'Pendentes',
    countKey: 'pendentes',
    icon: <Clock size={20} weight="duotone" />,
    accent: '#F59E0B',
    atencao: true,
  },
  {
    value: 'revisao',
    label: 'Revisão',
    countKey: 'revisao',
    icon: <Warning size={20} weight="duotone" />,
    accent: '#8B5CF6',
    atencao: true,
  },
];

const fmtNumero = (n: number) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface Props {
  resumo?: IContaCilLancamentoResumo;
  statusAtivo: FiltroStatus;
  onSelect: (value: FiltroStatus) => void;
  isLoading?: boolean;
}

/**
 * Faixa de KPIs da Conciliar: os 4 primeiros filtram por status (clicar no
 * ativo, exceto Total, volta pra Todos); o 5º (Líquido) é card-resumo de
 * moeda, não clicável.
 */
export function ConciliarKpis({ resumo, statusAtivo, onSelect, isLoading }: Props) {
  const total = resumo?.total ?? 0;
  const pctConciliado = total > 0 ? Math.round(((resumo?.conciliados ?? 0) / total) * 100) : 0;
  const somaTotal = resumo?.soma_total ?? 0;
  const pctClassificado =
    somaTotal > 0 ? Math.round(((resumo?.soma_classificado ?? 0) / somaTotal) * 100) : 0;

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4, '2xl': 8 }} spacing={4} mb={6}>
      {KPIS.map((kpi, idx) => {
        const ativo = statusAtivo === kpi.value;
        const count = resumo?.[kpi.countKey] ?? 0;
        const atencao = kpi.atencao && count > 0;
        return (
          <motion.div
            key={kpi.value || 'todos'}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.06 * idx, ease: [0.4, 0, 0.2, 1] }}
          >
            <GlassCard
              intensity="strong"
              hoverable
              p={5}
              h="full"
              cursor="pointer"
              role="button"
              tabIndex={0}
              aria-pressed={ativo}
              aria-label={`Filtrar por ${kpi.label}`}
              borderWidth={ativo ? '2px' : '1px'}
              borderColor={ativo ? kpi.accent : 'rgba(255, 255, 255, 0.6)'}
              onClick={() => onSelect(kpi.value)}
              onKeyDown={ev => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onSelect(kpi.value);
                }
              }}
            >
              <Flex direction="column" gap={2} h="full">
                <Flex align="center" justify="space-between">
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                  >
                    {kpi.label}
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="32px"
                    h="32px"
                    borderRadius="lg"
                    bg={`${kpi.accent}14`}
                    color={kpi.accent}
                  >
                    {kpi.icon}
                  </Box>
                </Flex>
                {isLoading && !resumo ? (
                  <Skeleton height="32px" width="60%" mt={1} borderRadius="md" />
                ) : (
                  <Text
                    fontSize={{ base: '24px', md: '28px' }}
                    fontWeight="700"
                    color="#0F172A"
                    letterSpacing="-0.02em"
                    lineHeight="1.1"
                  >
                    {fmtNumero(count)}
                  </Text>
                )}
                {kpi.value === 'conciliado' && total > 0 && (
                  <Text fontSize="11px" color={kpi.accent} fontWeight="600">
                    {pctConciliado}% do total
                  </Text>
                )}
                {atencao && (
                  <Text fontSize="11px" color={kpi.accent} fontWeight="600">
                    requer atenção
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        );
      })}

      {/* Moeda ocupa 2 colunas: valor com centavo nao cabe em 1. */}
      <Box key="liquido" gridColumn={{ lg: 'span 2' }}>
        <motion.div
          style={{ height: '100%' }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.06 * 4, ease: [0.4, 0, 0.2, 1] }}
        >
          <GlassCard intensity="strong" p={5} h="full">
            <Flex direction="column" gap={2} h="full">
              <Flex align="center" justify="space-between">
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                >
                  Líquido
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="32px"
                  h="32px"
                  borderRadius="lg"
                  bg="#1B88F514"
                  color="#1B88F5"
                >
                  <Scales size={20} weight="duotone" />
                </Box>
              </Flex>
              {isLoading && !resumo ? (
                <Skeleton height="32px" width="70%" mt={1} borderRadius="md" />
              ) : (
                // Card corta sem ellipsis: valor cortado vira numero errado.
                // overflowWrap quebra a linha em vez de cortar.
                <Text
                  fontSize={fonteValorKpi(fmtMoeda(resumo?.liquido ?? 0))}
                  fontWeight="700"
                  color="#0F172A"
                  letterSpacing="-0.02em"
                  lineHeight="1.1"
                  overflowWrap="anywhere"
                >
                  {fmtMoeda(resumo?.liquido ?? 0)}
                </Text>
              )}
              {resumo && (
                <Flex direction="column" gap="2px" fontSize="11px" fontWeight="600">
                  <Text color="#10B981" overflowWrap="anywhere">
                    + {fmtMoeda(resumo.soma_credito)}
                  </Text>
                  <Text color="#EF4444" overflowWrap="anywhere">
                    − {fmtMoeda(resumo.soma_debito)}
                  </Text>
                </Flex>
              )}
            </Flex>
          </GlassCard>
        </motion.div>
      </Box>

      {/* Composição — classificado (conta definitiva) vs provisório
          (a classificar / pendente). soma_classificado + soma_provisorio
          == soma_total. */}
      <Box key="composicao" gridColumn={{ lg: 'span 2' }}>
        <motion.div
          style={{ height: '100%' }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.06 * 5, ease: [0.4, 0, 0.2, 1] }}
        >
          <GlassCard intensity="strong" p={5} h="full">
            <Flex direction="column" gap={2} h="full">
              <Flex align="center" justify="space-between">
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                >
                  Composição
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="32px"
                  h="32px"
                  borderRadius="lg"
                  bg="#0EA5E914"
                  color="#0EA5E9"
                >
                  <Stack size={20} weight="duotone" />
                </Box>
              </Flex>
              {isLoading && !resumo ? (
                <Skeleton height="32px" width="70%" mt={1} borderRadius="md" />
              ) : (
                <>
                  <Flex
                    h="6px"
                    borderRadius="full"
                    overflow="hidden"
                    bg="rgba(15,23,42,0.06)"
                    mt={1}
                  >
                    <Box bg="#10B981" w={`${pctClassificado}%`} />
                    <Box bg="#F59E0B" w={`${100 - pctClassificado}%`} />
                  </Flex>
                  {/* Rotulo e valor empilhados: lado a lado o valor era cortado. */}
                  <Flex direction="column" gap="6px" fontSize="11px" fontWeight="600" mt={1}>
                    {[
                      {
                        lbl: 'Classificado',
                        cor: '#10B981',
                        texto: fmtMoeda(resumo?.soma_classificado ?? 0),
                        qtd: resumo?.qtd_classificado ?? 0,
                      },
                      {
                        lbl: 'Provisório',
                        cor: '#F59E0B',
                        texto: fmtMoeda(resumo?.soma_provisorio ?? 0),
                        qtd: resumo?.qtd_provisorio ?? 0,
                      },
                    ].map(l => (
                      <Box key={l.lbl}>
                        <Flex align="center" justify="space-between" gap={2}>
                          <Text color={l.cor}>{l.lbl}</Text>
                          <Text color="#94A3B8" flexShrink={0}>
                            {fmtNumero(l.qtd)}
                          </Text>
                        </Flex>
                        <Text
                          color="#0F172A"
                          fontSize={l.texto.length >= 17 ? '11px' : '13px'}
                          fontWeight="700"
                          lineHeight="1.2"
                          overflowWrap="anywhere"
                        >
                          {l.texto}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </>
              )}
            </Flex>
          </GlassCard>
        </motion.div>
      </Box>
    </SimpleGrid>
  );
}
